import React, { useEffect, useMemo, useState } from "react";

import { fetchAcquisitionHistory } from "../../services/investmentService";

/**
 * Inline buy / acquisition history (fundraising purchases + secondary transfers in).
 */
export default function HoldingAcquisitionHistory({ projectId, formatShare, formatMoney }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchAcquisitionHistory(projectId)
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.results || [];
        setRows(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "Could not load acquisition history.");
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const totals = useMemo(() => {
    let shares = 0;
    let amount = 0;
    for (const row of rows) {
      shares += Number(row.shares || 0);
      amount += Number(row.total_bdt || 0);
      if (!Number(row.total_bdt) && Number(row.shares) && Number(row.price_per_share)) {
        amount += Number(row.shares) * Number(row.price_per_share);
      }
    }
    return { shares, amount };
  }, [rows]);

  if (!projectId) return null;

  return (
    <section className="holding-acquisition-history" aria-labelledby={`acq-history-${projectId}`}>
      <div className="holding-acquisition-history__head">
        <h5 id={`acq-history-${projectId}`} className="holding-acquisition-history__title">
          Acquisition history
        </h5>
        {!loading && !error && rows.length > 0 ? (
          <div className="holding-acquisition-history__totals" aria-label="Acquisition totals">
            <div className="holding-acquisition-history__total">
              <span className="holding-acquisition-history__totals-label">Total SH</span>
              <span className="holding-acquisition-history__totals-value">{formatShare(totals.shares)}</span>
            </div>
            <div className="holding-acquisition-history__total">
              <span className="holding-acquisition-history__totals-label">Total BDT</span>
              <span className="holding-acquisition-history__totals-value">{formatMoney(totals.amount)}</span>
            </div>
          </div>
        ) : null}
      </div>

      {loading ? <p className="holding-acquisition-history__status muted">Loading acquisitions…</p> : null}
      {error ? <p className="holding-acquisition-history__status msg msg-error">{error}</p> : null}

      {!loading && !error && rows.length === 0 ? (
        <p className="holding-acquisition-history__status muted">No acquisition records for this project yet.</p>
      ) : null}

      {!loading && !error && rows.length > 0 ? (
        <div className="holding-acquisition-history__table-wrap">
          <table className="holding-acquisition-history__table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Date</th>
                <th scope="col">How acquired</th>
                <th scope="col" className="holding-acquisition-history__num">
                  Shares
                </th>
                <th scope="col" className="holding-acquisition-history__num">
                  Price / sh
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id || idx}>
                  <td>{idx + 1}</td>
                  <td className="holding-acquisition-history__date">
                    {row.requested_at ? new Date(row.requested_at).toLocaleString() : "—"}
                  </td>
                  <td>
                    <span
                      className={`holding-acquisition-history__kind${
                        row.record_kind === "SECONDARY_TRANSFER"
                          ? " holding-acquisition-history__kind--secondary"
                          : ""
                      }`}
                    >
                      {row.acquisition_label || "—"}
                    </span>
                  </td>
                  <td className="holding-acquisition-history__num">{formatShare(row.shares)}</td>
                  <td className="holding-acquisition-history__num">{formatMoney(Number(row.price_per_share || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
